import React, { useRef, useState } from 'react';
import { LuArrowLeft, LuExternalLink, LuLoader, LuLock, LuMicOff } from 'react-icons/lu';
import { Link, useNavigate } from 'react-router-dom';

import { PhoneCallDetail, ZoomPhoneCallPipelineStatus, ZoomPhoneCallState } from '@ndvr/customer-api';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/utils/cn';
import { formatDate, formatDateTime, useHouseholdRoutePaths, useRequiredParams } from 'utils';
import { useHouseholdTeamNameResolver } from 'views/admin/tenantTeam/tenant-team-queries';
import { useAddNoteMutation, useNoteQuery } from 'views/household/HouseholdNotes/note-queries';
import { getNotePersonName, MEETING_ASSISTANT_NAME } from 'views/household/HouseholdNotes/note-utils';
import { NoteBodyCard } from 'views/household/HouseholdNotes/NoteBodyCard';
import { formatPhone } from 'views/household/HouseholdTexts/text-utils';
import { PageSpinner, Text, Toast } from 'widgets';

import { AttachNoteToCallDialog } from './AttachNoteToCallDialog';
import {
    callRecordingUrl,
    useAttachNoteToCallMutation,
    useCallDetailQuery,
    useCallTranscriptQuery,
} from './call-queries';
import {
    CALL_STATE_META,
    callCounterpartyName,
    formatCallDuration,
    isOutbound,
    speakerColor,
    speakerInitials,
} from './call-utils';
import { CallAudioPlayer } from './CallAudioPlayer';
import { CallTranscript } from './CallTranscript';

const PartyAvatar = ({ name, className }: { name?: string; className?: string }) => {
    if (!name) return null;
    const color = speakerColor(name);
    return (
        <div
            className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-[11px] font-semibold',
                className,
            )}
            style={{ backgroundColor: `${color}29`, color }}
        >
            {speakerInitials(name)}
        </div>
    );
};

const footerNote = (state: ZoomPhoneCallState): string => {
    switch (state) {
        case ZoomPhoneCallState.RECORDED:
            return 'Recording and transcript synced and read-only, captured for compliance and recordkeeping.';
        case ZoomPhoneCallState.VOICEMAIL:
            return 'Voicemail and transcription synced and read-only, captured for compliance and recordkeeping.';
        default:
            return 'Call metadata synced and read-only. No recording exists for this call.';
    }
};

export const HouseholdCallPage = () => {
    const { globalAccountGUID, zoomPhoneCallGUID } = useRequiredParams(['globalAccountGUID', 'zoomPhoneCallGUID']);
    const navigate = useNavigate();
    const { MessagesTabRoutePath, NotesTabRoutePath } = useHouseholdRoutePaths();
    const detailQuery = useCallDetailQuery(zoomPhoneCallGUID);

    const detail = detailQuery.data;
    const call = detail?.call;
    const transcriptQuery = useCallTranscriptQuery(zoomPhoneCallGUID, !!call?.hasTranscript);
    const audioRef = useRef<HTMLAudioElement>(null);

    if (detailQuery.isLoading) {
        return <PageSpinner />;
    }

    if (!detail || !call) {
        return (
            <div className="flex items-center justify-center p-8">
                <Text $themeColor="negative">No call found for {zoomPhoneCallGUID}</Text>
            </div>
        );
    }

    const stateMeta = CALL_STATE_META[call.state];
    const counterpartyName = callCounterpartyName(call);
    const counterpartyNumber = isOutbound(call) ? call.toNumber : call.fromNumber;
    const segments = transcriptQuery.data?.segments ?? [];

    const seekRecording = (seconds: number) => {
        if (audioRef.current) {
            audioRef.current.currentTime = seconds;
        }
    };

    const metaFields = [
        { label: 'Direction', value: isOutbound(call) ? 'Outbound' : 'Inbound' },
        { label: 'Started', value: formatDateTime(call.startTime) },
        {
            label: 'Duration',
            value: call.durationSeconds ? formatCallDuration(call.durationSeconds) : 'No answer',
        },
        { label: 'Advisor', value: call.advisorName || '—' },
        { label: isOutbound(call) ? 'Dialed' : 'Caller', value: formatPhone(counterpartyNumber) || '—' },
    ];

    return (
        <div className="flex flex-col gap-4 p-4">
            <div className="flex items-center gap-3">
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Back to messages and calls"
                    onClick={() => navigate(MessagesTabRoutePath(globalAccountGUID))}
                >
                    <LuArrowLeft className="h-5 w-5" />
                </Button>
                <div className="mr-1 flex">
                    <PartyAvatar name={call.fromName || call.fromNumber} />
                    <PartyAvatar name={call.toName || call.toNumber} className="-ml-2" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-lg font-bold">
                        {call.state === ZoomPhoneCallState.VOICEMAIL ? 'Voicemail' : 'Phone call'}
                    </div>
                    <div className="text-muted-foreground truncate text-sm">
                        {[
                            counterpartyName,
                            formatPhone(counterpartyNumber),
                            isOutbound(call) ? 'Outbound' : 'Inbound',
                            formatDateTime(call.startTime),
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </div>
                </div>
                <Badge
                    variant="outline"
                    className={cn('flex-shrink-0 border-transparent tracking-wide uppercase', stateMeta.badgeClassName)}
                >
                    {stateMeta.label}
                </Badge>
            </div>

            <div className="border-border flex flex-col overflow-hidden rounded-xl border bg-white">
                {call.hasRecording && (
                    <CallAudioPlayer
                        src={callRecordingUrl(zoomPhoneCallGUID)}
                        downloadUrl={callRecordingUrl(zoomPhoneCallGUID, true)}
                        audioRef={audioRef}
                        fallbackDurationSeconds={call.durationSeconds}
                    />
                )}

                <div className="border-border grid grid-cols-2 gap-4 border-b px-6 py-4 md:grid-cols-5">
                    {metaFields.map((field) => (
                        <div key={field.label} className="flex min-w-0 flex-col gap-0.5">
                            <span className="text-muted-foreground text-[11px] tracking-wider uppercase">
                                {field.label}
                            </span>
                            <span className="truncate text-sm">{field.value}</span>
                        </div>
                    ))}
                </div>

                <div className="px-6 pb-4">
                    <CallContent
                        detail={detail}
                        segments={segments}
                        isTranscriptLoading={transcriptQuery.isLoading}
                        onSeek={call.hasRecording ? seekRecording : undefined}
                        globalAccountGUID={globalAccountGUID}
                        zoomPhoneCallGUID={zoomPhoneCallGUID}
                        notesViewPath={(noteGUID) =>
                            `${NotesTabRoutePath(globalAccountGUID)}/view?noteGUID=${noteGUID}`
                        }
                    />
                </div>

                <div className="border-border text-muted-foreground flex items-center gap-2 border-t bg-[#F8F7F5]/60 px-6 py-3 text-xs">
                    <LuLock className="h-3.5 w-3.5" />
                    <span>{footerNote(call.state)}</span>
                </div>
            </div>
        </div>
    );
};

const CallContent = ({
    detail,
    segments,
    isTranscriptLoading,
    onSeek,
    globalAccountGUID,
    zoomPhoneCallGUID,
    notesViewPath,
}: {
    detail: PhoneCallDetail;
    segments: React.ComponentProps<typeof CallTranscript>['segments'];
    isTranscriptLoading: boolean;
    onSeek?: (seconds: number) => void;
    globalAccountGUID: string;
    zoomPhoneCallGUID: string;
    notesViewPath: (noteGUID: string) => string;
}) => {
    const call = detail.call;
    const noteGUID = detail.noteGUIDs[0];
    const hasTranscript = call.hasTranscript;

    if (noteGUID && hasTranscript) {
        return (
            <Tabs defaultValue="note">
                <TabsList className="border-0 px-0">
                    <TabsTrigger value="note">Note</TabsTrigger>
                    <TabsTrigger value="transcript">Transcript</TabsTrigger>
                </TabsList>
                <TabsContent value="note">
                    <CallNotePane noteGUID={noteGUID} notesViewPath={notesViewPath} />
                </TabsContent>
                <TabsContent value="transcript">
                    <CallTranscriptPane
                        segments={segments}
                        isLoading={isTranscriptLoading}
                        advisorName={call.advisorName}
                        onSeek={onSeek}
                    />
                </TabsContent>
            </Tabs>
        );
    }

    if (noteGUID) {
        return (
            <div className="pt-4">
                <CallNotePane noteGUID={noteGUID} notesViewPath={notesViewPath} />
            </div>
        );
    }

    if (hasTranscript) {
        return (
            <CallTranscriptPane
                segments={segments}
                isLoading={isTranscriptLoading}
                advisorName={call.advisorName}
                onSeek={onSeek}
            />
        );
    }

    if (call.state === ZoomPhoneCallState.NOT_RECORDED || call.state === ZoomPhoneCallState.MISSED) {
        return (
            <UnrecordedCallContent
                detail={detail}
                globalAccountGUID={globalAccountGUID}
                zoomPhoneCallGUID={zoomPhoneCallGUID}
            />
        );
    }

    if (
        detail.pipelineStatus === ZoomPhoneCallPipelineStatus.PENDING_TRANSCRIPT ||
        detail.pipelineStatus === ZoomPhoneCallPipelineStatus.TRANSCRIBED
    ) {
        return (
            <div className="text-muted-foreground flex items-center gap-2 pt-4 text-sm">
                <LuLoader className="h-4 w-4 animate-spin" />
                <span>{`${MEETING_ASSISTANT_NAME}™ is processing this call — the transcript and note will appear here when ready.`}</span>
            </div>
        );
    }

    return <div className="text-muted-foreground pt-4 text-sm">No transcript is available for this call.</div>;
};

const CallTranscriptPane = ({
    segments,
    isLoading,
    advisorName,
    onSeek,
}: {
    segments: React.ComponentProps<typeof CallTranscript>['segments'];
    isLoading: boolean;
    advisorName?: string;
    onSeek?: (seconds: number) => void;
}) => {
    if (isLoading) {
        return (
            <div className="text-muted-foreground flex items-center gap-2 pt-4 text-sm">
                <LuLoader className="h-4 w-4 animate-spin" />
                <span>Loading transcript…</span>
            </div>
        );
    }
    if (segments.length === 0) {
        return <div className="text-muted-foreground pt-4 text-sm">No transcript is available for this call.</div>;
    }
    return <CallTranscript segments={segments} advisorName={advisorName} onSeek={onSeek} />;
};

const CallNotePane = ({
    noteGUID,
    notesViewPath,
}: {
    noteGUID: string;
    notesViewPath: (noteGUID: string) => string;
}) => {
    const noteQuery = useNoteQuery(noteGUID);
    const { nameFor } = useHouseholdTeamNameResolver(noteQuery.data?.householdGUID);

    if (noteQuery.isLoading) {
        return (
            <div className="text-muted-foreground flex items-center gap-2 pt-4 text-sm">
                <LuLoader className="h-4 w-4 animate-spin" />
                <span>Loading note…</span>
            </div>
        );
    }

    const note = noteQuery.data;
    if (!note) {
        return <div className="text-muted-foreground pt-4 text-sm">The attached note could not be loaded.</div>;
    }

    const personName = getNotePersonName(note.createdBy, nameFor);
    const updatedPersonName = note.updatedBy ? getNotePersonName(note.updatedBy, nameFor) : personName;

    return (
        <div className="flex flex-col gap-3 pt-2">
            <div className="flex items-center justify-between gap-4">
                <span className="truncate font-medium">{note.subject || 'Untitled Note'}</span>
                <Button asChild variant="outline" size="sm">
                    <Link to={notesViewPath(noteGUID)} className="flex items-center gap-1.5">
                        <LuExternalLink className="h-4 w-4" />
                        <span>Open note</span>
                    </Link>
                </Button>
            </div>
            <NoteBodyCard note={note} personName={personName} updatedPersonName={updatedPersonName} />
        </div>
    );
};

const UnrecordedCallContent = ({
    detail,
    globalAccountGUID,
    zoomPhoneCallGUID,
}: {
    detail: PhoneCallDetail;
    globalAccountGUID: string;
    zoomPhoneCallGUID: string;
}) => {
    const [noteContent, setNoteContent] = useState('');
    /** Set when the note was created but attaching it to the call failed — a retry only attaches. */
    const [savedNoteGUID, setSavedNoteGUID] = useState<string | null>(null);
    const [isAttachDialogOpen, setIsAttachDialogOpen] = useState(false);
    const addNoteMutation = useAddNoteMutation(globalAccountGUID);
    const attachMutation = useAttachNoteToCallMutation(zoomPhoneCallGUID);

    const isSaving = addNoteMutation.isPending || attachMutation.isPending;

    const handleSaveNote = async () => {
        let noteGUID = savedNoteGUID;
        try {
            if (!noteGUID) {
                const [createdNoteGUID] = await addNoteMutation.mutateAsync({
                    subject: `Call note — ${formatDate(detail.call.startTime)}`,
                    content: noteContent.trim(),
                    householdGUID: globalAccountGUID,
                });
                if (!createdNoteGUID) {
                    Toast.forError(new Error('createNote returned no note id'), 'Failed to save note.');
                    return;
                }
                noteGUID = createdNoteGUID;
                setSavedNoteGUID(createdNoteGUID);
            }
            await attachMutation.mutateAsync(noteGUID);
            Toast.success('Note saved and attached to call.');
            setSavedNoteGUID(null);
            setNoteContent('');
        } catch (error) {
            if (!noteGUID) {
                // Attach failures are toasted by the mutation; only creation failures need one here.
                Toast.forError(error, 'Failed to save note.');
            }
        }
    };

    return (
        <div className="flex flex-col gap-5 pt-5">
            <div className="border-border flex items-start gap-3.5 rounded-lg border border-dashed bg-[#F8F7F5] px-5 py-5">
                <LuMicOff className="text-muted-foreground mt-0.5 h-5 w-5 flex-shrink-0" />
                <div className="flex flex-col gap-1.5">
                    <span className="text-[15px] font-medium">This call was not recorded</span>
                    <span className="text-muted-foreground max-w-2xl text-sm leading-relaxed">
                        There is no audio or transcript to review. Call metadata above was captured automatically.
                        Recording is only available on lines where client consent is on file.
                    </span>
                </div>
            </div>

            <div className="flex flex-col gap-2.5">
                <span className="text-muted-foreground text-[13px] tracking-wider uppercase">Advisor notes</span>
                <Textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Summarize what was discussed, decisions made, and any follow-ups."
                    className="min-h-[120px] bg-white"
                    disabled={!!savedNoteGUID}
                />
                <div className="flex items-center gap-3">
                    <Button onClick={handleSaveNote} disabled={(!savedNoteGUID && !noteContent.trim()) || isSaving}>
                        {isSaving && <LuLoader className="h-4 w-4 animate-spin" />}
                        {savedNoteGUID ? 'Retry attaching note' : 'Save note'}
                    </Button>
                    <Button variant="outline" onClick={() => setIsAttachDialogOpen(true)} disabled={isSaving}>
                        Attach existing note
                    </Button>
                    <span className="text-muted-foreground text-[13px]">
                        {savedNoteGUID
                            ? 'Note saved to the household but not yet attached to this call.'
                            : noteContent
                              ? 'Unsaved changes'
                              : 'Notes appear in the Notes tab'}
                    </span>
                </div>
            </div>

            <AttachNoteToCallDialog
                open={isAttachDialogOpen}
                onOpenChange={setIsAttachDialogOpen}
                householdGUID={globalAccountGUID}
                zoomPhoneCallGUID={zoomPhoneCallGUID}
                attachedNoteGUIDs={detail.noteGUIDs}
            />
        </div>
    );
};
